import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import './CheeseChaseGame.css';

const CheeseChaseGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gameId, setGameId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    // Get or create game ID
    const existingGameId = searchParams.get('gameId');
    const currentGameId = existingGameId || `cheese-chase-game-${Date.now()}`;
    setGameId(currentGameId);

    // Load saved game if exists
    if (existingGameId) {
      // Load from localStorage using the quiz ID
      const data = localStorage.getItem('cheeseChaseQuizDatabase');
      const quizzes = data ? JSON.parse(data) : [];
      const quiz = quizzes.find(q => q.id === existingGameId);
      
      if (quiz && iframeRef.current) {
        // Send quiz data to iframe after a short delay
        setTimeout(() => {
          iframeRef.current.contentWindow.postMessage({
            type: 'loadQuiz',
            quiz: quiz
          }, '*');
        }, 1000);
      }
    }

    // Enter fullscreen
    const timer = setTimeout(() => {
      enterFullscreen();
    }, 500);

    // Listen for messages from iframe
    window.addEventListener('message', handleGameMessage);

    // Listen for fullscreen changes
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Auto-save on page unload
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('message', handleGameMessage);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [searchParams]);

  const enterFullscreen = async () => {
    try {
      if (containerRef.current) {
        if (containerRef.current.requestFullscreen) {
          await containerRef.current.requestFullscreen();
        } else if (containerRef.current.webkitRequestFullscreen) {
          await containerRef.current.webkitRequestFullscreen();
        } else if (containerRef.current.mozRequestFullScreen) {
          await containerRef.current.mozRequestFullScreen();
        } else if (containerRef.current.msRequestFullscreen) {
          await containerRef.current.msRequestFullscreen();
        }
        setIsFullscreen(true);
      }
    } catch (err) {
      console.log('Fullscreen request failed:', err);
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen();
      }
    } catch (err) {
      console.log('Exit fullscreen failed:', err);
    }
  };

  const handleFullscreenChange = () => {
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
    setIsFullscreen(isCurrentlyFullscreen);
  };

  const handleGameMessage = (event) => {
    // Listen for game state updates from iframe
    if (event.data && event.data.type === 'gameState') {
      const { currentQuestion, totalQuestions, score, title } = event.data;
      if (gameId) {
        saveGameData(gameId, {
          title: title || 'Cheese Chase Game',
          dateCreated: getGameData(gameId)?.dateCreated || new Date().toISOString(),
          progress: {
            currentQuestion,
            totalQuestions,
            score
          }
        });
      }
    }
  };

  const loadGameData = (id) => {
    try {
      const data = localStorage.getItem(id);
      if (data) {
        const gameData = JSON.parse(data);
        // Send loaded data to iframe
        if (iframeRef.current) {
          setTimeout(() => {
            iframeRef.current.contentWindow.postMessage({
              type: 'loadGame',
              data: gameData
            }, '*');
          }, 1000);
        }
      }
    } catch (error) {
      console.error('Error loading game:', error);
    }
  };

  const getGameData = (id) => {
    try {
      const data = localStorage.getItem(id);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      return null;
    }
  };

  const saveGameData = (id, data) => {
    try {
      localStorage.setItem(id, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  const handleManualSave = () => {
    setIsSaving(true);
    // Request current game state from iframe
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage({
        type: 'requestGameState'
      }, '*');
    }
    setTimeout(() => {
      setIsSaving(false);
      toast.success('Game saved!');
    }, 500);
  };

  const handleBeforeUnload = () => {
    // Auto-save before leaving
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage({
        type: 'requestGameState'
      }, '*');
    }
  };

  const handleExit = async () => {
    // Save before exit
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage({
        type: 'requestGameState'
      }, '*');
    }
    
    await exitFullscreen();
    setTimeout(() => {
      navigate('/game-hub');
    }, 300);
  };

  return (
    <div ref={containerRef} className="cheese-chase-game-page">
      {isFullscreen && (
        <div className="game-controls">
          <Button onClick={handleManualSave} className="save-btn" disabled={isSaving}>
            <Save size={18} />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          <Button onClick={handleExit} className="exit-btn">
            <ArrowLeft size={18} />
            Exit Game
          </Button>
        </div>
      )}
      
      {!isFullscreen && (
        <div className="game-header">
          <Button onClick={handleExit} className="back-btn" variant="ghost">
            <ArrowLeft size={20} />
            Back to Game Hub
          </Button>
        </div>
      )}

      <iframe
        ref={iframeRef}
        src="/cheese-chase.html"
        className="game-iframe"
        title="Cheese Chase Game"
        allow="fullscreen"
      />
    </div>
  );
};

export default CheeseChaseGame;
